
export const deletedUrl = "/deleted.json";

export type DeletedKind = "project" | "room" | "channel" | "channel_list" | "user";

export type DeletedAction = "restore" | "purge";

export interface Tombstone {
  id:				string;		// the selector for restore/purge; opaque
  kind:				string;
  key:				string;		// original identifier — this is what the user recognises
  name:				string;
  hotel:			string;		// owning project for rooms / local users; "" when global
  deleted_at:		number;		// unix SECONDS
  deleted_by:		string;
  retain_until:		number;		// unix SECONDS; 0 = never auto-purge
}
export const deletedPrivileges: { [kind: string]: string } = {
  project:			"DeleteProjects",
  channel:			"DeleteChannel",
  channel_list:		"DeleteGlobalChannelList",
  user:				"DeleteGlobalUsers",	// global users only
  room:				"",						// none — see above
};

export const deletedLocalUserPrivilege = "DeleteLocalUsers";

type JsonRow = Record<string, unknown>;

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumber(value: unknown): number {
  return typeof value === "number" ? value : 0;
}

export function narrowTombstones(body: unknown): Tombstone[] {
  const deleted = (body as { deleted?: unknown } | null | undefined)?.deleted;

  if (!Array.isArray(deleted))
    return [];

  return deleted
    .map((row: unknown) =>
      row && typeof row === "object" ? (row as JsonRow) : null
    )
    .filter((row: JsonRow | null): row is JsonRow =>
      !!row &&
      typeof row.id === "string" &&
      row.id !== "" &&
      typeof row.deleted_at === "number"
    )
    .map((row: JsonRow) => ({
      id:				asString(row.id),
      kind:				asString(row.kind),
      key:				asString(row.key),
      name:				asString(row.name),
      hotel:			asString(row.hotel),
      deleted_at:		asNumber(row.deleted_at),
      deleted_by:		asString(row.deleted_by),
      retain_until:		asNumber(row.retain_until),
    }));
}

export type DeletedListResult =
  | { kind: "ok";			items: Tombstone[] }
  | { kind: "unauthorized" }								// 401 — do NOT force a logout
  | { kind: "forbidden";	message: string }				// 403 — hide the section
  | { kind: "failure";		status: number; message: string };

export type DeletedActionResult =
  | { kind: "ok";			item: Tombstone | null }
  | { kind: "conflict";		message: string }				// 409 — inline, actionable
  | { kind: "gone";			message: string }				// 404 — already restored/purged
  | { kind: "forbidden";	message: string }				// 403
  | { kind: "unauthorized" }								// 401
  | { kind: "failure";		status: number; message: string };

async function readText(response: Response): Promise<string> {
  try {
    return (await response.text()).trim();
  } catch {
    return "";			// body already consumed or empty
  }
}

export async function listDeleted(kind: string, hotelId?: string): Promise<DeletedListResult> {
  const params = new URLSearchParams();

  params.set("kind", kind);


  if (hotelId)
    params.set("hotel_id", hotelId);

  let response: Response;

  try {
    response = await fetch(`${deletedUrl}?${params.toString()}`, {
      method:		"GET",
      credentials:	"include",
    });
  } catch (e) {
    console.error("listDeleted/failed:", e);
    return { kind: "failure", status: 0, message: "Network error" };
  }

  if (response.status === 200) {
    try {
      return { kind: "ok", items: narrowTombstones(await response.json()) };
    } catch (e) {
      console.error("listDeleted/malformed body:", e);
      return { kind: "failure", status: 200, message: "Malformed response" };
    }
  }

  const message = await readText(response);

  switch (response.status) {
  case 401:
    return { kind: "unauthorized" };

  case 403:
    return { kind: "forbidden", message: message };

  default:
    return {
      kind:		"failure",
      status:	response.status,
      message:	message || response.statusText || `Request failed (${response.status})`,
    };
  }
}

export async function actOnDeleted(
  kind:		string,
  id:		string,
  action:	DeletedAction,
  hotel?:	string,
): Promise<DeletedActionResult> {
  let response: Response;

  try {
    response = await fetch(deletedUrl, {
      method:		"POST",
      credentials:	"include",
      headers:		{ "Content-Type": "application/json" },
      body:			JSON.stringify({
        kind:		kind,
        id:			id,
        action:		action,
        ...(hotel ? { hotel: hotel } : {}),
      }),
    });
  } catch (e) {
    console.error("actOnDeleted/failed:", e);
    return { kind: "failure", status: 0, message: "Network error" };
  }

  if (response.status === 200) {
    // The tombstone acted on comes back so callers can reconcile optimistically.
    // A body we cannot parse is not a failure — the operation still succeeded.
    try {
      const body = await response.json();
      const rows = narrowTombstones({ deleted: [body] });

      return { kind: "ok", item: rows.length ? rows[0] : null };
    } catch {
      return { kind: "ok", item: null };
    }
  }

  const message = await readText(response);

  switch (response.status) {
  case 401:
    return { kind: "unauthorized" };

  case 403:
    return { kind: "forbidden", message: message };

  case 404:
    return { kind: "gone", message: message };

  case 409:
    return { kind: "conflict", message: message };

  default:
    return {
      kind:		"failure",
      status:	response.status,
      message:	message || response.statusText || `Request failed (${response.status})`,
    };
  }
}

// The deleted:error:conflict i18n key already reads "Cannot restore: {{m}}", so
// the server's 409 text deliberately does not repeat that prefix — except for
// the public_ip clash, whose message is self-contained. Render that one verbatim
// instead of wrapping it, or it reads "Cannot restore: cannot restore: …".
export function conflictIsSelfContained(message: string): boolean {
  return /^cannot restore/i.test(message.trim());
}
